import { Component, OnInit, AfterViewInit, Renderer2, Input } from '@angular/core';
import { LoaderService } from 'src/app/service/shared/loader/loader.service';
import { TableData } from 'src/app/shared/models/tableData';
import { JobPointeSettingsService } from 'src/app/service/job-pointe-settings/job-pointe-settings.service';
import { JobPointePermissionsService } from 'src/app/service/auth/job-pointe-permission.service';
import { BehaviorSubject } from 'rxjs';
import { MomentService } from 'src/app/service/shared/moment/moment.service';
import { JobPointeService } from 'src/app/service/job-pointe/job-pointe.service';

@Component({
  selector: 'app-job-history-list',
  templateUrl: './job-history-list.component.html',
  styleUrls: ['./job-history-list.component.scss'],
})
export class JobHistoryListComponent implements OnInit, AfterViewInit {
  constructor(
    private jobPointeSettingsService: JobPointeSettingsService,
    private jobPointService: JobPointeService,
    public renderer2: Renderer2,
    private loaderService: LoaderService,
    private jobPointePermissionsService: JobPointePermissionsService,
    private momentService: MomentService,
  ) { }

  @Input() row: any;
  requestParams: any;

  ngOnInit(): void {
    this.requestParams = {
      userId: this.row.id,
      order: 'createdAt,desc',
    };
  }

  private modalIsOpenSource = new BehaviorSubject<boolean>(false);
  modalIsOpen = this.modalIsOpenSource.asObservable();

  modelDetail: any = {
    'asset-classes': {},
    categories: {},
    manufacturers: {},
    enabled: {},
  };

  data: TableData = {
    label: 'Job History',
    type: 'jobHistory',
    editAccess: true,
    selectable: false,
    statusToggle: false,
    columns: [
      {
        name: 'ownerFacility',
        label: 'Owner | Facility',
        property: row => (row?.facility ? `${row?.facility?.owner?.ownerName} | ${row?.facility?.facilityName}` : ''),
        sortable: true,
        columnStyles: { 'max-width': '200px' },
        headerStyles: { 'min-width': '6rem' },
        columnClasses: { 'text-truncate': false },
      },
      {
        name: 'jobName',
        label: 'Job Name',
        property: row => row?.job?.name,
        sortable: true,
        columnStyles: { 'max-width': '200px', 'white-space': 'inherit' },
        headerStyles: { 'min-width': '3rem' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'mainForeman',
        label: 'Main Foreman',
        property: row =>
          row?.job?.jobBoardMainForeman?.split(',')?.join(', ') ||
          row?.facility?.jobBoardMainForeman?.split(',')?.join(', '),
        sortable: true,
        columnStyles: { 'max-width': '200px', 'text-align': '-webkit-center' },
        headerStyles: { 'min-width': '5rem', 'text-align': 'center' },
        columnClasses: { 'text-truncate': false },
      },
      {
        name: 'projectManager',
        label: 'Project Manager',
        property: row =>
          row?.job?.potentialJobDetail?.potentialJobs?.map(pj => pj.projectManager?.employeeAlias).join(', '),
        sortable: true,
        columnStyles: {
          'min-width': '100px',
          'max-width': '8%',
          'white-space': 'inherit',
        },
        headerStyles: { 'min-width': '8rem' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'createdAt',
        label: 'Date Assigned',
        property: row => this.momentService.formateDate(row?.createdAt),
        sortable: true,
        columnStyles: { 'max-width': '100px', 'white-space': 'inherit' },
        headerStyles: { 'min-width': '3rem' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'updatedAt',
        label: 'Date Unassigned',
        property: row => (row.status == 0 ? this.momentService.formateDate(row?.updatedAt) : ''),
        sortable: true,
        columnStyles: { 'max-width': '100px', 'white-space': 'inherit' },
        headerStyles: { 'min-width': '3rem' },
        columnClasses: { 'text-truncate': true },
      },
    ],
    rowActions: [
      {
        label: 'Archive',
        actionId: 'archiveEmployeeScheduleHistory',
        imageUrl: '',
        iconName: 'fa fa-solid fa-archive',
        show: row => {
          return this.jobPointePermissionsService.isAllowed(row, 'job_pointe_employees', 'edit');
        },
      },
    ],
    data: [],
    pagination: {},
    meta: {},
  };

  loadOptions(key: string): void {
    this.jobPointeSettingsService.read[key]().subscribe(
      res => {
        this.loaderService.hideLoader();
        const settings = res.data.reduce((list, item) => {
          list[item.id] = item.name;
          return list;
        }, {});
        this.modelDetail[key] = settings;
        // establish the list of options for the select elements
        const filter = this.filters.find(f => f.model === key);
        filter.options = res.data;
      },
      error => {
        console.error(error);
        this.loaderService.hideLoader();
      },
    );
  }

  ngAfterViewInit(): void {
    this.loadSelect2();
  }

  loadSelect2() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `loadSelect2();`;
  }

  filters: Array<any> = [
    {
      label: 'Filter by City',
      type: 'select2',
      propertyName: 'scope[city]',
      default: '',
      value: 'city',
      name: 'city',
      model: 'jobCities',
      options: [],
      loadOptions: () => this.loadOptions('jobCities'),
    },
    {
      label: 'Filter By Owner',
      type: 'select2',
      propertyName: 'scope[owner]',
      default: '',
      value: 'id',
      name: 'owner_name',
      model: 'owner',
      options: [],
      loadOptions: () => this.loadOptions('owner'),
    },
    {
      label: 'Filter by PM',
      type: 'select2',
      propertyName: 'scope[projectManager]',
      default: '',
      value: 'id',
      name: 'employeeAlias',
      model: 'projectManagers',
      options: [],
      loadOptions: () => this.loadOptions('projectManagers'),
    },
    {
      label: 'Filter by Date Assigned',
      type: 'date-range',
      propertyName: 'scope[dateAssigned]',
      default: '',
      value: 'id',
      name: 'label',
      model: 'dateAssigned',
      options: [],
    },
  ];
}
